import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Check, ArrowRight, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPRING = { type: 'spring', stiffness: 700, damping: 22, mass: 0.6 };
const DISMISS_KEY = 'newsletter-bar-dismissed';

/**
 * Sticky bottom subscribe bar — high-contrast red + dark, snappy.
 * Auto-hides on /admin and while inside the main Newsletter section, keeps
 * dismiss preference in sessionStorage.
 */
const StickyNewsletterBar = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && sessionStorage.getItem(DISMISS_KEY) === '1'
  );
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const onScroll = () => {
      // Show after 400px scroll so it doesn't fight the hero.
      setVisible(window.scrollY > 400);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const hidden = dismissed || pathname.startsWith('/admin') || pathname.startsWith('/apply');

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      await axios.post(`${API}/subscribe`, { email });
      setStatus('success');
      setMessage("You're in.");
      setEmail('');
      setTimeout(() => { setStatus('idle'); setMessage(''); dismiss(); }, 3000);
    } catch (err) {
      setStatus('error');
      if (err.response && err.response.status === 409) {
        setMessage("You're already subscribed.");
      } else {
        setMessage('Please try again.');
      }
    }
  };

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      {visible && !hidden && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={SPRING}
          className="fixed bottom-3 sm:bottom-4 inset-x-3 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 z-40 w-auto sm:w-[min(720px,calc(100vw-24px))]"
          data-testid="sticky-newsletter-bar"
        >
          <div
            className="relative rounded-full px-3 sm:px-5 py-2.5 border border-[#EF5B5B]/40"
            style={{
              background: 'linear-gradient(90deg, rgba(15,10,12,0.92), rgba(20,10,12,0.9))',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              boxShadow: '0 0 0 1px rgba(239,91,91,0.25), 0 20px 40px -10px rgba(239,91,91,0.35)',
            }}
          >
            <AnimatePresence mode="wait">
              {status === 'success' ? (
                <motion.div
                  key="ok"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={SPRING}
                  className="flex items-center justify-center gap-3 px-3 py-1.5"
                  data-testid="sticky-bar-success"
                >
                  <Check className="w-5 h-5 text-emerald-400" />
                  <p className="text-sm font-semibold text-white">{message}</p>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="flex items-center gap-2 sm:gap-3" data-testid="sticky-bar-form">
                  <span className="hidden sm:inline-flex items-center gap-2 pl-2 pr-1 text-[13px] font-bold text-white whitespace-nowrap">
                    <span className="w-2 h-2 rounded-full bg-[#EF5B5B] shadow-[0_0_10px_rgba(239,91,91,0.9)]" />
                    Join 1,500+ AI Revolutionaries
                  </span>
                  <span className="sm:hidden inline-flex items-center gap-1.5 pl-2 text-xs font-bold text-white">
                    <span className="w-2 h-2 rounded-full bg-[#EF5B5B] shadow-[0_0_8px_rgba(239,91,91,0.9)]" />
                    Subscribe
                  </span>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="sticky-bar-email"
                    className="flex-1 min-w-0 bg-black/40 border border-white/10 focus:border-[#EF5B5B]/60 rounded-full text-white placeholder-gray-500 focus:outline-none px-4 py-2 text-sm"
                  />
                  <motion.button
                    type="submit"
                    disabled={status === 'loading'}
                    data-testid="sticky-bar-submit"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={SPRING}
                    className="shrink-0 inline-flex items-center gap-1.5 px-4 sm:px-5 py-2 btn-red rounded-full text-sm font-bold disabled:opacity-70"
                  >
                    {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                      <>
                        Subscribe
                        <ArrowRight className="w-4 h-4 hidden sm:inline" />
                      </>
                    )}
                  </motion.button>
                  <button
                    type="button"
                    onClick={dismiss}
                    data-testid="sticky-bar-dismiss"
                    aria-label="Dismiss subscribe bar"
                    className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/5 smooth"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </form>
              )}
            </AnimatePresence>

            {status === 'error' && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-[11px] text-[#EF5B5B] pl-4 pt-1"
              >
                {message}
              </motion.p>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StickyNewsletterBar;
